import {loggerFactory} from "@cm/browser-log"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ConfigInfo, Parameters, SceneNodes} from "@cm/template-nodes"
import {isInstanceOf} from "@cm/utils"
import {hashObject} from "@cm/utils/hashing"
import {Observable} from "rxjs"

const allVariationsLogger = loggerFactory.getLogger("templates")

export type ConfigurationInfo = {
    name: string
    parameters: Parameters
    hash: string
    scene: SceneNodes.SceneNode[]
}

export function gatherAllVariations(
    localSceneManagerService: SceneManagerService,
    scope: "local" | "global",
    progress?: (progress: number) => void,
    configInfoSideEffect?: (configInfo: ConfigInfo) => void,
) {
    return new Observable<ConfigurationInfo | "done">((observer) => {
        let isAborted = false

        ;(async () => {
            type PermutationProposal = Record<string, string>

            const allConfigurations = new Set<string>()
            const visitedProposals = new Set<string>()
            const pendingProposals: PermutationProposal[] = [{}]
            let countUpperBound = 1

            progress?.(0)
            while (true) {
                if (isAborted) break

                const proposal = pendingProposals.shift()
                if (!proposal) break

                localSceneManagerService.$instanceParameters.set(new Parameters({parameters: proposal}))

                localSceneManagerService.compileTemplate()
                try {
                    await localSceneManagerService.sync(true)
                } catch (e) {
                    if (isAborted) break
                    throw e
                }

                const currentConfiguration =
                    scope === "global" ? localSceneManagerService.$currentGlobalConfiguration() : localSceneManagerService.$currentLocalConfiguration()
                const hash = currentConfiguration.getHash()

                if (!allConfigurations.has(hash)) {
                    const descriptors = localSceneManagerService
                        .getDescriptorsForNode(localSceneManagerService.$templateInstance())
                        .filter(isInstanceOf(ConfigInfo))

                    // Check the variant id we carried forward from a previous compile is not valid in this configuration's variants list
                    // This can happen for context-sensitive (id-sharing) configuration groups
                    const hasInvalidProposedKey = descriptors.some(
                        (d) => proposal[d.props.id] !== undefined && d.props.value === null && !d.props.isSetByTemplate,
                    )

                    const curConfigs: PermutationProposal = {}

                    const configNames: string[] = []
                    for (const descriptor of descriptors) {
                        const {id, name, value, isSetByTemplate} = descriptor.props

                        if (!value || isSetByTemplate) continue

                        curConfigs[id] = value.id
                        configNames.push(`${name}: ${value.name}`)
                    }

                    if (!hasInvalidProposedKey) {
                        const newConfiguration: ConfigurationInfo = {
                            name: configNames.length > 0 ? configNames.join(", ") : "Single Variation",
                            parameters: currentConfiguration.clone({cloneSubNode: () => true}),
                            hash,
                            scene: localSceneManagerService.$scene(),
                        }

                        observer.next(newConfiguration)
                        allConfigurations.add(hash)
                    }

                    for (const descriptor of descriptors) {
                        const {id, variants, isSetByTemplate} = descriptor.props

                        if (isSetByTemplate) continue

                        for (const variant of variants.filter((x) => !x.excludeFromPermutations)) {
                            const proposal: PermutationProposal = {
                                ...curConfigs,
                                [id]: variant.id,
                            }

                            const key = hashObject(proposal)
                            if (!visitedProposals.has(key)) {
                                countUpperBound += 1
                                visitedProposals.add(key)
                                pendingProposals.push(proposal)
                            }
                        }
                    }

                    if (configInfoSideEffect) descriptors.forEach(configInfoSideEffect)

                    await new Promise((resolve) => setTimeout(resolve, 0))
                }

                allVariationsLogger.debug(`Proposals remaining: ${pendingProposals.length} / ${countUpperBound}`)
                progress?.(((countUpperBound - pendingProposals.length) / countUpperBound) * 100)
            }

            observer.next("done")
            observer.complete()
        })()

        return () => {
            isAborted = true
        }
    })
}
